import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState
} from '@microsoft/signalr';
import { Subject } from 'rxjs';
import {
  CreativeSetFollowedPayload,
  CreativeSetUnfollowedPayload,
  NotificationReceivedEvent
} from '../domain';
import { NS_CONFIG_TOKEN } from '../notification.types';
import { FollowStateChanged } from '../domain/followstate.notifications.types';

@Injectable()
export class SignalRService {
  private readonly config = inject(NS_CONFIG_TOKEN);
  private connection: HubConnection | undefined;

  private _newNotification$ = new Subject<NotificationReceivedEvent>();
  newNotification$ = this._newNotification$.asObservable();

  private _followStateChanged$ = new Subject<FollowStateChanged>();
  followStateChanged$ = this._followStateChanged$.asObservable();

  private accessToken: string | undefined;

  constructor() {
    if (!this.config.signalR.enabled) {
      return;
    }

    this.config.accessToken$
      .pipe(takeUntilDestroyed())
      .subscribe(accessToken => {
        if (accessToken) {
          this.accessToken = accessToken;
          this.createConnection();
        }
      });
  }

  async startConnection(): Promise<void> {
    if (
      !this.connection ||
      this.connection.state === HubConnectionState.Disconnected
    ) {
      await this.connection?.start();
    }
  }

  private async createConnection(): Promise<void> {
    const signalRHubUrl = this.config.signalR.url;

    if (this.connection) {
      return;
    }

    this.connection = new HubConnectionBuilder()
      .withUrl(signalRHubUrl, {
        accessTokenFactory: () => this.getAccessToken(),
        withCredentials: true
      })
      .withAutomaticReconnect()
      .build();

    this.registerListeners();

    await this.startConnection();
  }

  private registerListeners(): void {
    if (!this.connection) {
      return;
    }
    this.connection.on(
      'NotificationReceived',
      (eventData: NotificationReceivedEvent) => {
        this._newNotification$.next(eventData);
      }
    );

    this.connection.on(
      'CreativeSetFollowed',
      (eventData: CreativeSetFollowedPayload) => {
        this._followStateChanged$.next({
          creativeSetId: eventData.creativeSetId,
          isFollowing: true
        });
      }
    );

    this.connection.on(
      'CreativeSetUnfollowed',
      (eventData: CreativeSetUnfollowedPayload) => {
        this._followStateChanged$.next({
          creativeSetId: eventData.creativeSetId,
          isFollowing: false
        });
      }
    );
  }
  private getAccessToken(): Promise<string> {
    if (!this.accessToken) {
      return Promise.reject(new Error('No access token'));
    }
    return Promise.resolve(this.accessToken);
  }
}
